import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { transferResponseSchema, transferStatusSchema, transfersResponseSchema, type Transfer, type TransferStatus } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { SignatureCanvas } from '../sign/SignatureCanvas.js';
import { formatDate } from '../issuance/FormsPage.js';
import { STOREROOM_ROLES } from '../../components/RequireRole.js';

const input = 'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm';
const primary = 'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary = 'rounded-lg px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-40';
const danger = 'rounded-lg bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-40';

/**
 * Transfers in the caller's reach (§3.2 as revised 2026-09-21): who is
 * handing what to whom, and where it stands. Once the receiver has signed,
 * someone above everyone involved signs it off here — approve or decline;
 * the server says who may (`canApprove`). The storeroom may cancel one the
 * receiver has not signed yet, or — with a comment and their *own*
 * signature — complete it by attestation. That is the only override, and
 * it never records a signature the receiver did not make.
 *
 * The status filter lives in the URL so a notification can land on
 * "awaiting approval" directly.
 */
export function TransfersPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { roles } = useClaimRoles();
  const [params, setParams] = useSearchParams();
  const parsed = transferStatusSchema.safeParse(params.get('status'));
  const status: TransferStatus | '' = params.has('status') ? (parsed.success ? parsed.data : '') : 'pending_receiver';
  const setStatus = (next: TransferStatus | '') => setParams({ status: next }, { replace: true });
  const state = useApi(`/transfers${status ? `?status=${status}` : ''}`, transfersResponseSchema);
  const storeroom = roles.some((r) => STOREROOM_ROLES.includes(r));

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="transfers-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{t('transfers.title')}</h2>
        <label className="text-xs text-slate-600">
          {t('transfers.statusLabel')}
          <select className="ms-2 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm" value={status} onChange={(e) => setStatus(e.target.value as TransferStatus | '')} data-testid="transfers-status">
            <option value="">{t('transfers.any')}</option>
            {transferStatusSchema.options.map((s) => (
              <option key={s} value={s}>{t(`transfers.status.${s}`)}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('transfers.hint')}</p>

      {state.kind === 'loading' && <p className="mt-3 text-slate-500">{t('home.loading')}</p>}
      {state.kind === 'error' && <div className="mt-3"><ApiError code={state.code} requestId={state.requestId} /></div>}
      {state.kind === 'ready' && state.data.transfers.length === 0 && (
        <p className="mt-3 text-sm text-slate-500" data-testid="transfers-empty">{t('transfers.empty')}</p>
      )}
      {state.kind === 'ready' && (
        <ul className="mt-3 divide-y divide-slate-100">
          {state.data.transfers.map((tr) => (
            <TransferRow
              key={tr.id}
              transfer={tr}
              language={i18n.language}
              canCancel={tr.status === 'pending_receiver' && (storeroom || tr.initiatedBy === user?.uid || tr.fromUid === user?.uid)}
              canOverride={tr.status === 'pending_receiver' && storeroom}
              onChanged={state.reload}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TransferRow({
  transfer: tr,
  language,
  canCancel,
  canOverride,
  onChanged,
}: {
  transfer: Transfer;
  language: string;
  canCancel: boolean;
  canOverride: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [panel, setPanel] = useState<'cancel' | 'override' | 'approve' | 'decline' | null>(null);
  const tone =
    tr.status === 'pending_receiver' || tr.status === 'pending_approval' ? 'bg-amber-50 text-amber-800 ring-amber-200'
    : tr.status === 'completed' ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : tr.status === 'overridden' ? 'bg-red-50 text-red-800 ring-red-200'
    : 'bg-slate-100 text-slate-600 ring-slate-200';

  return (
    <li className="py-3" data-testid="transfer-row" data-id={tr.id} data-status={tr.status}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-slate-900">
            {language.startsWith('en') ? tr.nameEn : tr.nameHe}
            {tr.serialNumber ? (
              <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{tr.serialNumber}</span>
            ) : (
              <span className="ms-2 text-sm text-slate-500">× <span className="latin-inline">{tr.quantity}</span></span>
            )}
          </p>
          <p className="text-sm text-slate-600">{t('transfers.fromTo', { from: tr.fromName, to: tr.toName })}</p>
          {tr.reason && <p className="text-sm text-slate-500">{tr.reason}</p>}
          {tr.attestation && (
            <p className="mt-1 text-xs text-red-700" data-testid="transfer-attestation">
              {t('transfers.attestedBy', { by: tr.attestation.byName })} · {tr.attestation.comment}
            </p>
          )}
          {tr.status === 'pending_approval' && (
            <p className="mt-1 text-xs text-amber-800" data-testid="transfer-needs-level">
              {t('transfers.needsLevel', { who: t(`transfers.level.${Math.min(3, Math.max(1, tr.approvalLevel))}`) })}
            </p>
          )}
          {tr.approval && (
            <p className="mt-1 text-xs text-emerald-700" data-testid="transfer-approval">
              {t('transfers.approvedBy', { by: tr.approval.byName })}
              {tr.approval.comment && ` · ${tr.approval.comment}`}
            </p>
          )}
          {tr.closeReason && (tr.status === 'declined' || tr.status === 'cancelled') && (
            <p className="mt-1 text-xs text-slate-500" data-testid="transfer-close-reason">
              {t('transfers.closeReason', { reason: tr.closeReason })}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
          <span className={`rounded px-1.5 py-0.5 ring-1 ${tone}`}>{t(`transfers.status.${tr.status}`)}</span>
          <span className="latin-inline">{formatDate(tr.createdAt, language)}</span>
          <Link to={`/forms/${tr.formId}`} className="text-slate-700 underline-offset-4 hover:underline">{t('alerts.openForm')}</Link>
        </div>
      </div>
      {(canCancel || canOverride || tr.canApprove) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {tr.canApprove && (
            <>
              <button type="button" onClick={() => setPanel(panel === 'approve' ? null : 'approve')} className={primary} data-testid="transfer-approve-open">
                {t('transfers.approve')}
              </button>
              <button type="button" onClick={() => setPanel(panel === 'decline' ? null : 'decline')} className={secondary} data-testid="transfer-decline-open">
                {t('transfers.decline')}
              </button>
            </>
          )}
          {canCancel && (
            <button type="button" onClick={() => setPanel(panel === 'cancel' ? null : 'cancel')} className={secondary} data-testid="transfer-cancel-open">
              {t('transfers.cancel')}
            </button>
          )}
          {canOverride && (
            <button type="button" onClick={() => setPanel(panel === 'override' ? null : 'override')} className={danger} data-testid="transfer-override-open">
              {t('transfers.override')}
            </button>
          )}
        </div>
      )}
      {panel === 'cancel' && <CancelPanel transfer={tr} onDone={() => { setPanel(null); onChanged(); }} />}
      {panel === 'override' && <OverridePanel transfer={tr} onDone={() => { setPanel(null); onChanged(); }} />}
      {panel === 'approve' && <ApprovePanel transfer={tr} language={language} onDone={() => { setPanel(null); onChanged(); }} />}
      {panel === 'decline' && <DeclinePanel transfer={tr} onDone={() => { setPanel(null); onChanged(); }} />}
    </li>
  );
}

/** The sign-off (owner, 2026-09-21): no drawn signature — the ledger entry under the approver's uid is the record. */
function ApprovePanel({ transfer, language, onDone }: { transfer: Transfer; language: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const act = useAction(() => apiPost(`/transfers/${transfer.id}/approve`, transferResponseSchema, { comment: comment.trim() || null }));
  return (
    <div className="mt-2 rounded-xl bg-emerald-50 p-3 ring-1 ring-emerald-200" data-testid="transfer-approve-panel">
      <p className="text-sm text-emerald-900">
        {t('transfers.approveHint', { item: language.startsWith('en') ? transfer.nameEn : transfer.nameHe, from: transfer.fromName, to: transfer.toName })}
      </p>
      <label className="mt-2 block text-sm text-slate-700">
        {t('transfers.approveComment')}
        <input className={input} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="transfer-approve-comment" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy} className={`mt-3 ${primary}`} data-testid="transfer-approve-submit">
        {t('transfers.approveSubmit')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </div>
  );
}

function DeclinePanel({ transfer, onDone }: { transfer: Transfer; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const act = useAction(() => apiPost(`/transfers/${transfer.id}/decline`, transferResponseSchema, { reason: reason.trim() }));
  return (
    <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid="transfer-decline-panel">
      <label className="block text-sm text-slate-600">
        {t('transfers.declineReason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-decline-reason" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy || reason.trim().length < 3} className={`mt-3 ${danger}`} data-testid="transfer-decline-submit">
        {t('transfers.declineSubmit')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </div>
  );
}

function CancelPanel({ transfer, onDone }: { transfer: Transfer; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const act = useAction(() => apiPost(`/transfers/${transfer.id}/cancel`, transferResponseSchema, { reason: reason.trim() }));
  return (
    <div className="mt-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200" data-testid="transfer-cancel-panel">
      <label className="block text-sm text-slate-600">
        {t('transfers.cancelReason')}
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-cancel-reason" />
      </label>
      <button type="button" onClick={() => void act.run().then((r) => r && onDone())} disabled={act.busy || reason.trim().length < 3} className={`mt-3 ${secondary}`} data-testid="transfer-cancel-submit">
        {t('transfers.cancelSubmit')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </div>
  );
}

/**
 * The attestation. The canvas here is the admin's own signature, sent as
 * an attestation — the server stores it under `attestations/`, never as the
 * receiver's signature, and the form goes `attested`, never `signed`.
 */
function OverridePanel({ transfer, onDone }: { transfer: Transfer; onDone: () => void }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const onInk = useCallback((dataUrl: string | null) => setImage(dataUrl), []);
  const act = useAction(() => apiPost(`/transfers/${transfer.id}/override`, transferResponseSchema, { comment: comment.trim(), imageDataUrl: image }));

  return (
    <div className="mt-2 rounded-xl bg-red-50 p-3 ring-1 ring-red-200" data-testid="transfer-override-panel">
      <p className="text-sm text-red-900">{t('transfers.overrideHint', { to: transfer.toName })}</p>
      <label className="mt-2 block text-sm text-slate-700">
        {t('transfers.overrideComment')}
        <textarea className={input} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="transfer-override-comment" />
      </label>
      <p className="mt-2 text-sm text-slate-700">{t('transfers.overrideSignature')}</p>
      <div className="mt-1">
        <SignatureCanvas onChange={onInk} disabled={act.busy} />
      </div>
      <button
        type="button"
        onClick={() => void act.run().then((r) => r && onDone())}
        disabled={act.busy || comment.trim().length < 3 || !image}
        className={`mt-3 ${danger}`}
        data-testid="transfer-override-submit"
      >
        {t('transfers.overrideSubmit')}
      </button>
      <div className="mt-2"><ActionError error={act.error} /></div>
    </div>
  );
}
